import { useState, useEffect } from "react";

type ChannelType = "slack" | "discord" | "teams";

interface NotificationChannel {
  id: string;
  type: ChannelType;
  name: string;
  webhookUrl: string;
  enabled: boolean;
}

const STORAGE_KEY = "atlas_notification_channels";

const CHANNEL_LABELS: Record<ChannelType, string> = {
  slack: "Slack",
  discord: "Discord",
  teams: "Microsoft Teams",
};

const CHANNEL_PLACEHOLDER: Record<ChannelType, string> = {
  slack: "https://hooks.slack.com/services/…",
  discord: "https://discord.com/api/webhooks/…",
  teams: "https://your-org.webhook.office.com/webhookb2/…",
};

function loadChannels(): NotificationChannel[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as NotificationChannel[]) : [];
  } catch {
    return [];
  }
}

function saveChannels(channels: NotificationChannel[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(channels));
}

function genId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export default function SettingsPage() {
  const [channels, setChannels] = useState<NotificationChannel[]>(loadChannels);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<{ type: ChannelType; name: string; webhookUrl: string }>({
    type: "slack",
    name: "",
    webhookUrl: "",
  });
  const [formError, setFormError] = useState<string | null>(null);

  // Persist on every change
  useEffect(() => {
    saveChannels(channels);
  }, [channels]);

  function handleAdd() {
    if (!form.name.trim()) {
      setFormError("Name is required.");
      return;
    }
    if (!form.webhookUrl.trim()) {
      setFormError("Webhook URL is required.");
      return;
    }
    try {
      new URL(form.webhookUrl.trim());
    } catch {
      setFormError("Webhook URL must be a valid URL.");
      return;
    }

    setChannels((prev) => [
      ...prev,
      {
        id: genId(),
        type: form.type,
        name: form.name.trim(),
        webhookUrl: form.webhookUrl.trim(),
        enabled: true,
      },
    ]);
    setForm({ type: "slack", name: "", webhookUrl: "" });
    setFormError(null);
    setShowForm(false);
  }

  function handleToggle(id: string) {
    setChannels((prev) =>
      prev.map((c) => (c.id === id ? { ...c, enabled: !c.enabled } : c))
    );
  }

  function handleDelete(id: string) {
    setChannels((prev) => prev.filter((c) => c.id !== id));
  }

  return (
    <div className="space-y-8 max-w-2xl">
      <h1 className="text-xl font-semibold">Settings</h1>

      {/* Notification Channels */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-white">Notification Channels</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Webhook endpoints for Slack, Discord, or Microsoft Teams alerts.
            </p>
          </div>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="text-xs px-3 py-1.5 rounded bg-gray-800 text-gray-200 hover:bg-gray-700 transition-colors"
            >
              + Add channel
            </button>
          )}
        </div>

        {/* Add form */}
        {showForm && (
          <div className="border border-gray-700 rounded p-4 space-y-3 bg-gray-900">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs text-gray-400">Platform</label>
                <select
                  value={form.type}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, type: e.target.value as ChannelType }))
                  }
                  className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white focus:outline-none focus:border-gray-500"
                >
                  <option value="slack">Slack</option>
                  <option value="discord">Discord</option>
                  <option value="teams">Microsoft Teams</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-gray-400">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. #alerts-prod"
                  className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-500"
                />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-gray-400">Webhook URL</label>
              <input
                type="url"
                value={form.webhookUrl}
                onChange={(e) => setForm((f) => ({ ...f, webhookUrl: e.target.value }))}
                placeholder={CHANNEL_PLACEHOLDER[form.type]}
                className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-500"
              />
            </div>
            {formError && (
              <p className="text-xs text-red-400">{formError}</p>
            )}
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setShowForm(false);
                  setFormError(null);
                  setForm({ type: "slack", name: "", webhookUrl: "" });
                }}
                className="text-xs px-3 py-1.5 rounded text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                className="text-xs px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-500 transition-colors"
              >
                Add channel
              </button>
            </div>
          </div>
        )}

        {/* Channel list */}
        {channels.length > 0 ? (
          <div className="space-y-2">
            {channels.map((ch) => (
              <div
                key={ch.id}
                className={`flex items-center gap-3 border border-gray-800 rounded px-3 py-2.5 bg-gray-900 ${
                  ch.enabled ? "" : "opacity-50"
                }`}
              >
                <ChannelIcon type={ch.type} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white">{ch.name}</span>
                    <span className="text-xs text-gray-500">{CHANNEL_LABELS[ch.type]}</span>
                  </div>
                  <span className="text-xs text-gray-600 truncate block">
                    {ch.webhookUrl}
                  </span>
                </div>
                <button
                  onClick={() => handleToggle(ch.id)}
                  className={`text-xs px-2 py-1 rounded transition-colors ${
                    ch.enabled
                      ? "bg-green-900 text-green-400 hover:bg-green-800"
                      : "bg-gray-800 text-gray-500 hover:bg-gray-700"
                  }`}
                >
                  {ch.enabled ? "Enabled" : "Disabled"}
                </button>
                <button
                  onClick={() => handleDelete(ch.id)}
                  className="text-xs text-gray-600 hover:text-red-400 transition-colors px-1"
                  title="Remove channel"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        ) : (
          !showForm && (
            <p className="text-xs text-gray-600 border border-dashed border-gray-800 rounded px-4 py-6 text-center">
              No notification channels configured.
            </p>
          )
        )}

        <p className="text-xs text-gray-600">
          Channel configuration is stored locally in this browser. Delivery requires
          matching webhook secrets set on the Worker via{" "}
          <code className="text-gray-500">wrangler secret put</code>.
        </p>
      </section>
    </div>
  );
}

function ChannelIcon({ type }: { type: ChannelType }) {
  const base = "w-6 h-6 rounded flex items-center justify-center text-xs font-bold shrink-0";
  if (type === "slack")
    return <span className={`${base} bg-purple-900 text-purple-300`}>S</span>;
  if (type === "discord")
    return <span className={`${base} bg-indigo-900 text-indigo-300`}>D</span>;
  return <span className={`${base} bg-blue-900 text-blue-300`}>T</span>;
}
